import { useUser } from '@myosh/myosh-login';
import {
  type DataGridDataRowTemplateProps,
  type DataGridRef,
  type DropDownResult,
  type JsonDataItem,
  type RowTemplateType,
  useScreenSize,
} from '@myosh/odin-components';
import clsx from 'clsx';
import React, { lazy, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Subject, takeUntil } from 'rxjs';
import type { Module } from '../@types/modules';
import type { RecordResult } from '../@types/records';
import AllFormsGrid from '../components/dynamic-page/all-forms-grid';
import OdinCalendar from '../components/dynamic-page/odin-calendar';
import SingleFormGrid from '../components/dynamic-page/single-form-grid';
import LazyLoadedPage from '../components/layout/routes/lazy-loaded-page.component';
import { RecordTitle } from '../components/record/record-title.component';
import { constructRecordTitle } from '../components/record/util/record-component-util';
import { type OdinDBRecordMutationMetadata } from '../db/odin-db-types';
import { isDbRecordId } from '../db/odin-db-util';
import useActiveRecordRef from '../hooks/use-active-record-ref';
import useDoubleClick from '../hooks/use-double-click';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { useLazyCheckRecordOfflineAvailabilityQuery } from '../redux/services/db-api';
import { clearSelectedForm, dynamicPageState } from '../redux/slices/dynamic-page-slice';
import { selectActiveRecordId } from '../redux/slices/layout';
import { getDataGridReference } from '../services/data-grid.service';
import { showInfo } from '../services/notification.service';
import { onlineStatusService } from '../services/online-status.service';
import {
  processAuthorFilterForContinuousScroller,
  processLastEditedFilterForContinousScroller,
  processRecordFilterForContinuousScroller,
} from '../utils/dynamic-page-utils';

interface DynamicPageProps {
  selectedForm: DropDownResult;
  module: Module;
  onViewChange?: (hasChanged: boolean) => void;
  isCalendarView?: boolean;
}

const AllFormsGridMobile = lazy(() => import('../components/dynamic-page/mobile-all-forms-grid'));
const SingleFormGridMobile = lazy(() => import('../components/dynamic-page/mobile-single-form-grid'));

export default function DynamicPage({ selectedForm, module, onViewChange, isCalendarView = false }: DynamicPageProps) {
  const gridRef = useRef<DataGridRef | undefined>(undefined);
  const destroySubject = useRef<Subject<void> | undefined>(undefined);

  const dispatch = useAppDispatch();
  const { selectedFilters } = useAppSelector(dynamicPageState);
  const isAllForms = selectedForm.value === 0;
  const activeRecordId = useAppSelector(selectActiveRecordId);
  const { state: userState } = useUser();
  const { activeRecordReference } = useActiveRecordRef();
  const { rowClickHandler } = useDoubleClick();
  const { isMobile } = useScreenSize();
  const { t } = useTranslation();

  const [checkRecordOfflineAvailability] = useLazyCheckRecordOfflineAvailabilityQuery();

  useEffect(() => {
    if (activeRecordId) {
      gridRef.current?.api.data.addSelectedRows([{ id: activeRecordId }], true);
    }
  }, [activeRecordId]);

  const onDataLoadCompleted = useCallback(() => {
    if (activeRecordId) {
      gridRef.current?.api.data.addSelectedRows([{ id: activeRecordId }], true);
    }
  }, [activeRecordId]);

  useEffect(() => {
    destroySubject.current = new Subject<void>();
    getDataGridReference()
      .pipe(takeUntil(destroySubject.current))
      .subscribe((dataGridRef) => {
        if (dataGridRef) {
          gridRef.current = dataGridRef;
        }
      });

    return () => {
      destroySubject.current?.next();
      destroySubject.current?.complete();

      dispatch(clearSelectedForm());
    };
  }, [dispatch]);

  useEffect(() => {
    if (gridRef.current && selectedFilters.records) {
      gridRef.current.api.filters.addFilter(processRecordFilterForContinuousScroller(selectedFilters.records));
    }
  }, [selectedFilters.records]);

  useEffect(() => {
    if (gridRef.current && selectedFilters.author) {
      if (selectedFilters.author.value === 'any') {
        gridRef.current.api.filters.removeFilter('author');
      } else {
        gridRef.current.api.filters.addFilter(processAuthorFilterForContinuousScroller(selectedFilters.author));
      }
    }
  }, [selectedFilters.author]);

  useEffect(() => {
    if (gridRef.current && !isAllForms && selectedFilters.lastEdited) {
      gridRef.current.api.filters.addFilter(processLastEditedFilterForContinousScroller(selectedFilters.lastEdited));
    }
  }, [selectedFilters.lastEdited, isAllForms]);

  const clickHandler = useCallback(
    async (item: JsonDataItem | RecordResult, isFullScreen: boolean) => {
      const recordId = item.id;
      const docNo = String(item.docNo);
      const status = String(item.status);
      const formName = isAllForms ? String(item.moduleForm) : String(item.formName);
      const date = item.creationDate ? new Date(item.creationDate as string) : undefined;
      const title = constructRecordTitle(formName, docNo, status);
      const details = <RecordTitle title={title} date={date} />;

      activeRecordReference.current?.addActiveRecord(String(recordId), module.icon, details, isFullScreen, {
        title,
        recordId,
        viewId: module.viewId,
        moduleIcon: module.icon,
        formId: item.formId,
        formName: formName,
        docNo,
        status,
        vikingLinkUrl: item.vikingLinkUrl,
        displayText: item.displayText,
      });
    },
    [selectedForm?.value, module.viewId, userState.user?.currentSchema]
  );

  const rowTemplate: RowTemplateType = useCallback(
    (props: DataGridDataRowTemplateProps, children: React.ReactNode | Array<React.ReactNode>) => {
      const recordId = props.data?.id;

      let isOfflineRecordPending = false,
        isOfflineRecordError = false;
      const isDbRecord = isDbRecordId(recordId as string | number);
      if (isDbRecord) {
        const { state } = (props.data?._offlineMetadata ?? {}) as OdinDBRecordMutationMetadata;
        isOfflineRecordPending = state === 'pending';
        isOfflineRecordError = state === 'error' || state === 'conflict';
      }

      const handleClick = async (isFullScreen: boolean) => {
        if (!isDbRecord && !onlineStatusService.getStatus()) {
          let isAvailable = false;

          try {
            isAvailable = await checkRecordOfflineAvailability(Number(recordId)).unwrap();
          } catch {
            // ignore db error and just prevent showing the record
          }

          if (!isAvailable) {
            showInfo(t('offline-no-data', 'Unable to retrieve data while offline'), { id: 'record-offline-no-data' });
            return;
          }
        }

        clickHandler(props.data, isFullScreen);
      };

      const handleRowClick = () => {
        rowClickHandler(handleClick);
      };

      return (
        <div className={clsx('relative flex w-full', { 'opacity-75': isDbRecord })} onClick={handleRowClick}>
          {isDbRecord && (
            <div
              className={clsx('absolute left-0 top-0 h-0 w-0 border-r-[12px] border-t-[12px] border-r-transparent', {
                'border-t-gray-2': isOfflineRecordPending,
                'border-t-error': isOfflineRecordError,
              })}
            />
          )}
          <div className="flex flex-grow items-center" onClick={props.onRowClicked}>
            {children}
          </div>
        </div>
      );
    },
    [clickHandler, selectedFilters.records]
  );

  if (isCalendarView) {
    return (
      <OdinCalendar
        selectedForm={selectedForm}
        onDataLoadCompleted={onDataLoadCompleted}
        activeRecordReference={activeRecordReference}
        rowClickHandler={rowClickHandler}
        module={module}
        isAllForms={isAllForms}
        selectedFilters={selectedFilters}
      />
    );
  }

  if (isMobile) {
    return (
      <LazyLoadedPage
        page={
          isAllForms ? (
            <AllFormsGridMobile
              selectedForm={selectedForm}
              moduleId={module.id}
              selectionHandler={clickHandler}
              onViewChange={onViewChange}
            />
          ) : (
            <SingleFormGridMobile
              rowTemplate={rowTemplate}
              selectedForm={selectedForm}
              moduleId={module.id}
              selectionHandler={clickHandler}
              onViewChange={onViewChange}
            />
          )
        }
      />
    );
  }

  return (
    <>
      {isAllForms ? (
        <AllFormsGrid
          rowTemplate={rowTemplate}
          menuItems={[]}
          selectedForm={selectedForm}
          moduleId={module.id}
          onDataLoadCompleted={onDataLoadCompleted}
          onViewChange={onViewChange}
        />
      ) : (
        <SingleFormGrid
          rowTemplate={rowTemplate}
          menuItems={[]}
          selectedForm={selectedForm}
          moduleId={module.id}
          onDataLoadCompleted={onDataLoadCompleted}
          onViewChange={onViewChange}
        />
      )}
    </>
  );
}
